import { KeyCode } from 'keyboardevent-codes';
import { useCallback, useEffect, useState } from 'react';

import { useCodeKeyMap } from '../useCodeKeyMap';
import {
  KeyboardKeyCombination,
  KeyboardKeyCombinationBase,
  KeyboardModifierKeyCode,
  normativeOrderKeyCombination,
} from '../utils/keyboardUtils';

const eventModifierKeys = {
  ctrlKey: 'Control',
  metaKey: 'Meta',
  altKey: 'Alt',
  shiftKey: 'Shift',
} as const satisfies Record<
  keyof Pick<KeyboardEvent, 'ctrlKey' | 'metaKey' | 'altKey' | 'shiftKey'>,
  KeyboardModifierKeyCode
>;

/** The options passed to {@link useKeyCombinationRecorder} */
export type UseKeyCombinationRecorderOptions = {
  /**
   * An array of keys that if pressed will stop recording and mark it as
   * accepted.
   */
  readonly acceptKeys?: KeyCode[];
  /**
   * An array of keys that if pressed will stop recording and mark it as
   * rejected.
   */
  readonly rejectKeys?: KeyCode[];
};

type StopRecordingOptions = {
  /** Whether the recording has been rejected. */
  readonly isRejected?: boolean;
};

export type StartRecordingFn = () => void;

export type StopRecordingFn = (options?: StopRecordingOptions) => void;

export type ResetRecordingFn = () => void;

/** The status of the recorder */
export type RecordingStatus =
  | 'uninitialized'
  | 'recording'
  | 'accepted'
  | 'rejected';

/** The result of the {@link useKeyCombinationRecorder} hook */
export type UseKeyCombinationRecorderResult = {
  /** The current status of the recorder. */
  readonly recordingStatus: RecordingStatus;
  /** The currently recorded key combination. */
  readonly keyCombination?: KeyboardKeyCombination;
  /**
   * The function that resets the recording process. Useful when using single
   * hook for several key combinations.
   */
  readonly resetRecording: ResetRecordingFn;
  /** The function that starts the recording process. */
  readonly startRecording: StartRecordingFn;
  /** The function that stops the recording process. */
  readonly stopRecording: StopRecordingFn;
};

/**
 * A hook that allows you to capture keyboard combinations.
 *
 * @param options The hook options.
 */
export function useKeyCombinationRecorder(
  options: UseKeyCombinationRecorderOptions = {},
): UseKeyCombinationRecorderResult {
  const { acceptKeys = [], rejectKeys = [] } = options;
  const [recordingStatus, setRecordingStatus] =
    useState<RecordingStatus>('uninitialized');
  const [keyCombination, setKeyCombination] =
    useState<KeyboardKeyCombination>();
  const codeKeyMap = useCodeKeyMap();

  const startRecording = useCallback<StartRecordingFn>(() => {
    setKeyCombination(undefined);
    setRecordingStatus('recording');
  }, []);

  const stopRecording = useCallback<StopRecordingFn>((options = {}) => {
    const { isRejected } = options;
    setRecordingStatus(isRejected ? 'rejected' : 'accepted');
  }, []);

  const resetRecording = useCallback<ResetRecordingFn>(() => {
    setKeyCombination(undefined);
    setRecordingStatus('uninitialized');
  }, []);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.repeat) {
      return;
    }

    const eventHasModifiers = event.getModifierState(event.key);
    const isAcceptedKey =
      !eventHasModifiers && acceptKeys.includes(event.code as KeyCode);
    const isRejectedKey =
      !eventHasModifiers && rejectKeys.includes(event.code as KeyCode);

    if (isAcceptedKey || isRejectedKey) {
      stopRecording({ isRejected: isRejectedKey });
    } else {
      setKeyCombination(getKeyCombinationFromKeyboardEvent(event, codeKeyMap));
    }
  };

  // Handles event listeners.
  useEffect(() => {
    if (recordingStatus === 'recording') {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // We don't want to change event listeners when `handleKeydown` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingStatus]);

  return {
    resetRecording,
    startRecording,
    stopRecording,
    recordingStatus,
    keyCombination,
  };
}

/**
 * Gets the key combination from keyboard event.
 *
 * @param event The keyboard event.
 * @param codeKeyMap The code to key map.
 */
function getKeyCombinationFromKeyboardEvent(
  event: KeyboardEvent,
  codeKeyMap: Record<string, string> = {},
): KeyboardKeyCombination<KeyboardModifierKeyCode> {
  const eventHasModifiers = event.getModifierState(event.key);
  const code = eventHasModifiers
    ? undefined
    : (event.code as KeyboardKeyCombination['binding'][1]);

  const key = eventHasModifiers
    ? undefined
    : getKeyFromEvent(event, codeKeyMap);

  const modifiers = Object.keys(eventModifierKeys).reduce<
    readonly KeyboardModifierKeyCode[]
  >((mods, currentModifier) => {
    if (event[currentModifier]) {
      return mods.concat(eventModifierKeys[currentModifier]);
    }

    return mods;
  }, [] as readonly KeyboardModifierKeyCode[]);

  const binding: KeyboardKeyCombinationBase<KeyCode, KeyboardModifierKeyCode> =
    [modifiers, code];
  const presentation: KeyboardKeyCombinationBase<
    string,
    KeyboardModifierKeyCode
  > = [modifiers, key];

  const keyCombination = { binding, presentation };

  return normativeOrderKeyCombination(keyCombination);
}

/**
 * Gets the key string from keyboard event
 *
 * @param event The keyboard event.
 * @param codeKeyMap The code to key map.
 */
function getKeyFromEvent(
  event: KeyboardEvent,
  codeKeyMap: Record<string, string> = {},
): string {
  const keyFromCodeMap: string | undefined =
    codeKeyMap[event.code]?.toLocaleUpperCase();

  if (keyFromCodeMap) {
    return keyFromCodeMap;
  }

  if (/^(Key|Digit)/.test(event.code)) {
    return String.fromCharCode(event.keyCode);
  }

  return event.code;
}
